import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { Label } from './ui/label'
import { Settings, Check, Loader2, AlertCircle } from 'lucide-react'
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import {
  useUpdateConfigurationMutation,
  useUpdateWorkspacesMutation,
  useUpdateSpacesMutation,
  useGetClickupConfigQuery,
  useLazyGetClickupTokenQuery,
} from '@/redux/service/configuration'
import type { ClickupWorkspace, ClickupSpace } from '@/redux/service/datascope'
import toast from 'react-hot-toast'

const TokenSchema = Yup.object().shape({
  token: Yup.string().required('ClickUp API Token is required to proceed.'),
})

const WorkspacesSchema = Yup.object().shape({
  selectedWorkspaces: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string().required(),
        name: Yup.string().required(),
      })
    )
    .min(1, 'Please select at least one workspace to map.'),
})

const SpacesSchema = Yup.object().shape({
  selectedSpaces: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string().required(),
        name: Yup.string().required(),
        archived: Yup.boolean(),
      })
    )
    .min(1, 'Please select at least one space to map.'),
})

interface ConfigFormValues {
  token: string
  selectedWorkspaces: ClickupWorkspace[]
  selectedSpaces: ClickupSpace[]
}

interface ConfigurationModeProps {
  isOpen: boolean
  isClose: () => void
}

interface TokenApiResponse {
  message: string
  data: ClickupWorkspace[]
}

interface WorkspacesApiResponse {
  message: string
  data: ClickupSpace[]
}

const ConfigurationMode = ({ isOpen, isClose }: ConfigurationModeProps) => {
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [workspaces, setWorkspaces] = useState<ClickupWorkspace[]>([])
  const [spaces, setSpaces] = useState<ClickupSpace[]>([])

  const [updateConfiguration] = useUpdateConfigurationMutation()
  const [updateWorkspaces] = useUpdateWorkspacesMutation()
  const [updateSpaces] = useUpdateSpacesMutation()
  const [fetchToken] = useLazyGetClickupTokenQuery()

  const { data: configData, isFetching: isFetchingConfig } =
    useGetClickupConfigQuery(undefined, {
      skip: !isOpen || step !== 1,
    })

  useEffect(() => {
    if (isOpen) {
      setStep(1)
      setWorkspaces([])
      setSpaces([])
    }
  }, [isOpen])

  const initialValues: ConfigFormValues = {
    token: '',
    selectedWorkspaces: [],
    selectedSpaces: [],
  }

  const handleTokenSave = async (
    values: ConfigFormValues,
    actions: FormikHelpers<ConfigFormValues>
  ) => {
    try {
      const res = (await updateConfiguration({
        api_token: values.token,
      }).unwrap()) as TokenApiResponse
      if (res?.data && Array.isArray(res.data)) {
        setWorkspaces(res.data)
        actions.setFieldValue('selectedWorkspaces', res.data)
      }
      setStep(2)
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to save token')

      actions.setFieldError(
        'token',
        'Failed to authenticate token. Please try again.'
      )
    } finally {
      actions.setSubmitting(false)
    }
  }

  const handleTokenSkip = async (
    setFieldValue: (
      field: string,
      value: string | ClickupWorkspace[] | ClickupSpace[]
    ) => void
  ) => {
    try {
      const tokenRes = await fetchToken().unwrap()
      if (tokenRes?.data) {
        setFieldValue('token', tokenRes.data)
      }
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to fetch existing token')
    }

    // Use existing workspaces from GET configs if available
    let existingWorkspaces: ClickupWorkspace[] = []
    if (configData?.data?.workspaces) {
      existingWorkspaces = configData.data.workspaces
    } else if (configData?.data && Array.isArray(configData.data)) {
      existingWorkspaces = configData.data
    }

    setWorkspaces(existingWorkspaces)
    setFieldValue('selectedWorkspaces', existingWorkspaces)
    setStep(2)
  }

  const handleWorkspacesSave = async (
    values: ConfigFormValues,
    actions: FormikHelpers<ConfigFormValues>
  ) => {
    try {
      const res = (await updateWorkspaces({
        api_token: values.token,
        workspaces: values.selectedWorkspaces,
      }).unwrap()) as WorkspacesApiResponse
      if (res?.data && Array.isArray(res.data)) {
        setSpaces(res.data)
        actions.setFieldValue('selectedSpaces', res.data)
      }
      setStep(3)
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to save workspaces')

      actions.setFieldError(
        'selectedWorkspaces',
        'Failed to save workspaces mapping.'
      )
    } finally {
      actions.setSubmitting(false)
    }
  }

  const handleSpacesSave = async (
    values: ConfigFormValues,
    actions: FormikHelpers<ConfigFormValues>
  ) => {
    try {
      const res = await updateSpaces({
        api_token: values.token,
        workspaces: values.selectedWorkspaces,
        spaces: values.selectedSpaces,
      }).unwrap()
      toast.success(
        res.message || 'Clickup configuration updated successfully!'
      )
      isClose()
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to save spaces')
      actions.setFieldError('selectedSpaces', 'Failed to save spaces mapping.')
    } finally {
      actions.setSubmitting(false)
    }
  }

  // Determine current schema & submit handler
  const currentSchema =
    step === 1 ? TokenSchema : step === 2 ? WorkspacesSchema : SpacesSchema

  const handleNextStep = (
    values: ConfigFormValues,
    actions: FormikHelpers<ConfigFormValues>
  ) => {
    if (step === 1) handleTokenSave(values, actions)
    else if (step === 2) handleWorkspacesSave(values, actions)
    else if (step === 3) handleSpacesSave(values, actions)
  }

  return (
    <Dialog open={isOpen} onOpenChange={isClose}>
      <DialogContent className='max-w-[550px] flex flex-col p-0 overflow-hidden bg-[#F5F5F7] border-[#D2D2D7]/50 shadow-xl sm:rounded-2xl'>
        <DialogHeader className='p-6 pb-4 border-b border-[#D2D2D7]/40 bg-white/80 backdrop-blur-xl'>
          <DialogTitle className='text-xl font-semibold flex items-center gap-2.5 tracking-tight text-[#1D1D1F]'>
            <div className='p-1.5 bg-accent  text-primary rounded-lg shadow-sm'>
              <Settings className='w-5 h-5' />
            </div>
            {step === 1 && 'Configure Data'}
            {step === 2 && 'Map Workspaces'}
            {step === 3 && 'Map Spaces'}
          </DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={initialValues}
          validationSchema={currentSchema}
          onSubmit={handleNextStep}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form className='p-6 flex-1 flex flex-col bg-white'>
              {step === 1 && (
                <div className='flex-1 flex flex-col justify-between animate-in fade-in slide-in-from-right-4 duration-300 space-y-8'>
                  <div className='space-y-3'>
                    <Label
                      htmlFor='token'
                      className='text-sm font-medium text-[#1D1D1F]'
                    >
                      ClickUp API Token <span className='text-red-500'>*</span>
                    </Label>
                    <p className='text-xs text-red-500 mt-1'>
                      Note: If you update workspaces or spaces using the same
                      API token, the data will simply update. If you change the
                      API token, all existing synced data will be removed and
                      fresh data will be synced.
                    </p>
                    <Field
                      as={Input}
                      id='token'
                      name='token'
                      placeholder='Enter your personal access token'
                      className={`w-full rounded-xl transition-all duration-200 border-[#D2D2D7] bg-[#F5F5F7] focus-visible:bg-white focus-visible:ring-[#0071E3]/30 focus-visible:border-[#0071E3] ${
                        errors.token && touched.token
                          ? 'border-red-500/50 focus-visible:ring-red-500/20 focus-visible:border-red-500'
                          : ''
                      }`}
                    />
                    <ErrorMessage
                      name='token'
                      component='div'
                      className='text-[13px] text-red-500 font-medium flex items-center gap-1.5 mt-1.5'
                    />
                  </div>

                  <div className='flex items-center justify-end gap-3 pt-4 border-t border-[#F5F5F7]'>
                    <Button
                      type='button'
                      variant='ghost'
                      onClick={isClose}
                      className='text-[#86868B] hover:text-[#1D1D1F] rounded-xl hover:bg-[#F5F5F7]'
                    >
                      Cancel
                    </Button>
                    <Button
                      type='button'
                      variant='secondary'
                      onClick={() => handleTokenSkip(setFieldValue)}
                      className='rounded-xl bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E5E5EA] border border-[#D2D2D7]/50 shadow-sm'
                    >
                      Skip
                    </Button>
                    <Button
                      type='submit'
                      disabled={isSubmitting}
                      className='rounded-xl text-white shadow-sm hover:shadow-md transition-all'
                    >
                      {isSubmitting && (
                        <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                      )}
                      Save & Continue
                    </Button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className='flex-1 flex flex-col justify-between animate-in fade-in slide-in-from-right-4 duration-300 space-y-6'>
                  <div className='space-y-3 flex-1'>
                    <div className='flex items-center justify-between '>
                      <Label className='text-sm font-medium text-[#1D1D1F]'>
                        Available Workspaces
                      </Label>
                      {workspaces.length > 0 && (
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='h-8 text-[12px] font-semibold text-[#0071E3] hover:bg-[#0071E3]/5 rounded-lg'
                          onClick={() => {
                            const allSelected =
                              values.selectedWorkspaces.length ===
                              workspaces.length
                            setFieldValue(
                              'selectedWorkspaces',
                              allSelected ? [] : workspaces
                            )
                          }}
                        >
                          {values.selectedWorkspaces.length ===
                          workspaces.length
                            ? 'Deselect All'
                            : 'Select All'}
                        </Button>
                      )}
                    </div>

                    {isFetchingConfig ? (
                      <div className='flex flex-col items-center justify-center py-12 text-[#86868B] space-y-3'>
                        <Loader2 className='w-6 h-6 animate-spin text-[#0071E3]' />
                        <span className='text-sm font-medium'>
                          Loading workspaces...
                        </span>
                      </div>
                    ) : workspaces.length === 0 ? (
                      <div className='text-[13px] text-[#86868B] p-6 border border-dashed border-[#D2D2D7] rounded-2xl bg-[#F5F5F7] flex flex-col justify-center items-center text-center space-y-2'>
                        <AlertCircle className='w-5 h-5 opacity-50' />
                        <p>No workspaces found for this token.</p>
                      </div>
                    ) : (
                      <div className='grid gap-2.5 max-h-[300px] overflow-y-auto pr-2 '>
                        {workspaces.map(ws => {
                          const isSelected = values.selectedWorkspaces.some(
                            s => s.id === ws.id
                          )
                          return (
                            <div
                              key={ws.id}
                              onClick={() => {
                                const exists = values.selectedWorkspaces.find(
                                  p => p.id === ws.id
                                )
                                const newSelection = exists
                                  ? values.selectedWorkspaces.filter(
                                      p => p.id !== ws.id
                                    )
                                  : [...values.selectedWorkspaces, ws]
                                setFieldValue(
                                  'selectedWorkspaces',
                                  newSelection
                                )
                              }}
                              className={`flex items-center justify-between p-3.5 border rounded-xl cursor-pointer transition-all duration-200 ${
                                isSelected
                                  ? 'border-[#0071E3] bg-[#0071E3]/5 text-[#0071E3] shadow-sm '
                                  : 'border-[#D2D2D7]/60 hover:border-[#D2D2D7] hover:bg-[#F5F5F7] hover:shadow-sm'
                              }`}
                            >
                              <span className='font-medium text-[14px]'>
                                {ws.name}
                              </span>
                              <div
                                className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                                  isSelected
                                    ? 'bg-[#0071E3] border-[#0071E3]'
                                    : 'border-[#C7C7CC]'
                                }`}
                              >
                                {isSelected && (
                                  <Check className='w-3 h-3 text-white stroke-[3]' />
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                    {typeof errors.selectedWorkspaces === 'string' && (
                      <div className='text-[13px] text-red-500 font-medium flex items-center gap-1.5 mt-2'>
                        {errors.selectedWorkspaces}
                      </div>
                    )}
                  </div>

                  <div className='flex items-center justify-between pt-4 border-t border-[#F5F5F7]'>
                    <Button
                      type='button'
                      variant='ghost'
                      onClick={() => {
                        setWorkspaces([])
                        setFieldValue('selectedWorkspaces', [])
                        setStep(1)
                      }}
                      className='text-[#86868B] hover:text-[#1D1D1F] rounded-xl hover:bg-[#F5F5F7]'
                    >
                      ← Back
                    </Button>
                    <div className='flex items-center gap-3'>
                      <Button
                        type='button'
                        variant='ghost'
                        onClick={isClose}
                        className='text-[#86868B] hover:text-[#1D1D1F] rounded-xl hover:bg-[#F5F5F7]'
                      >
                        Cancel
                      </Button>
                      <Button
                        type='submit'
                        disabled={isSubmitting}
                        className='rounded-xl bg-[#0071E3] hover:bg-[#0077ED] text-white shadow-sm hover:shadow-md transition-all'
                      >
                        {isSubmitting && (
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                        )}
                        Save Workspaces
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className='flex-1 flex flex-col justify-between animate-in fade-in slide-in-from-right-4 duration-300 space-y-6'>
                  <div className='space-y-3 flex-1'>
                    <div className='flex items-center justify-between'>
                      <Label className='text-sm font-medium text-[#1D1D1F]'>
                        Available Spaces
                      </Label>
                      {spaces.length > 0 && (
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='h-8 text-[12px] font-semibold text-[#0071E3] hover:bg-[#0071E3]/5 rounded-lg'
                          onClick={() => {
                            const allSelected =
                              values.selectedSpaces.length === spaces.length
                            setFieldValue(
                              'selectedSpaces',
                              allSelected ? [] : spaces
                            )
                          }}
                        >
                          {values.selectedSpaces.length === spaces.length
                            ? 'Deselect All'
                            : 'Select All'}
                        </Button>
                      )}
                    </div>
                    {spaces.length === 0 ? (
                      <div className='text-[13px] text-[#86868B] p-6 border border-dashed border-[#D2D2D7] rounded-2xl bg-[#F5F5F7] flex flex-col justify-center items-center text-center space-y-2'>
                        <AlertCircle className='w-5 h-5 opacity-50' />
                        <p>No spaces found in mapped workspaces.</p>
                      </div>
                    ) : (
                      <div className='grid gap-2.5 max-h-[350px] overflow-y-auto pr-2 '>
                        {spaces.map(sp => {
                          const isSelected = values.selectedSpaces.some(
                            s => s.id === sp.id
                          )
                          return (
                            <div
                              key={sp.id}
                              onClick={() => {
                                const exists = values.selectedSpaces.find(
                                  p => p.id === sp.id
                                )
                                const newSelection = exists
                                  ? values.selectedSpaces.filter(
                                      p => p.id !== sp.id
                                    )
                                  : [...values.selectedSpaces, sp]
                                setFieldValue('selectedSpaces', newSelection)
                              }}
                              className={`flex items-center justify-between p-3.5 border rounded-xl cursor-pointer transition-all duration-200 ${
                                isSelected
                                  ? 'border-[#0071E3] bg-[#0071E3]/5 text-[#0071E3] shadow-sm'
                                  : 'border-[#D2D2D7]/60 hover:border-[#D2D2D7] hover:bg-[#F5F5F7] hover:shadow-sm'
                              }`}
                            >
                              <div className='flex flex-col items-start'>
                                <span className='font-medium text-[14px]'>
                                  {sp.name}
                                </span>
                                {sp.archived && (
                                  <span className='text-[10px] uppercase font-bold text-[#86868B] tracking-wider mt-0.5'>
                                    Archived
                                  </span>
                                )}
                              </div>
                              <div
                                className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                                  isSelected
                                    ? 'bg-[#0071E3] border-[#0071E3]'
                                    : 'border-[#C7C7CC]'
                                }`}
                              >
                                {isSelected && (
                                  <Check className='w-3 h-3 text-white stroke-[3]' />
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                    {typeof errors.selectedSpaces === 'string' && (
                      <div className='text-[13px] text-red-500 font-medium flex items-center gap-1.5 mt-2'>
                        {errors.selectedSpaces}
                      </div>
                    )}
                  </div>

                  <div className='flex items-center justify-between pt-4 border-t border-[#F5F5F7]'>
                    <Button
                      type='button'
                      variant='ghost'
                      onClick={() => {
                        setSpaces([])
                        setFieldValue('selectedSpaces', [])
                        setStep(2)
                      }}
                      className='text-[#86868B] hover:text-[#1D1D1F] rounded-xl hover:bg-[#F5F5F7]'
                    >
                      ← Back
                    </Button>
                    <div className='flex items-center gap-3'>
                      <Button
                        type='button'
                        variant='ghost'
                        onClick={isClose}
                        className='text-[#86868B] hover:text-[#1D1D1F] rounded-xl hover:bg-[#F5F5F7]'
                      >
                        Cancel
                      </Button>
                      <Button
                        type='submit'
                        disabled={isSubmitting}
                        className='rounded-xl bg-[#0071E3] hover:bg-[#0077ED] text-white shadow-sm hover:shadow-md transition-all'
                      >
                        {isSubmitting && (
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                        )}
                        Finish
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default ConfigurationMode
